import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { getHasSearchedForCurrentQuery, getProjectsOfResultsGroupedByActiveState } from './search-results-reducer';
import Search from '../search-main/Search';
import ProfileInfo from '../../user/profile-info/ProfileInfo';
import { trackPageView } from '../../support-and-tracking/analytics-actions';
import Main from '../../frame/main/Main';
import { fetchCMSAllEntriesByContentType } from '../../cms/cms-actions';
import SearchCampaignBanner from '../../campaign/campaign-banner/SearchCampaignBanner';
import { CMS_CONTENT_TYPE_CAMPAIGN } from '../../campaign/campaign-banner/CampaignConstants';
import ProjectCardList from '../../project/project-card-list/ProjectCardList';
import ShowMore from '../../components/show-more/ShowMore';
import styles from './search-with-results.module.less';
import GenericHelmet from '../../project/generic-helmet/GenericHelmet';
import { Heading1, Heading2, Heading3 } from '@sb1/ffe-core-react';

const SearchWithResults = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const query = queryString.parse(location.search);
    const results = useSelector(getProjectsOfResultsGroupedByActiveState);
    const hasSearchedForCurrentQuery = useSelector(getHasSearchedForCurrentQuery);

    useEffect(() => {
        trackPageView({ urlAsEvent: 'search' });
    }, []);

    useEffect(() => {
        if (results.singleProject && !isNaN(query?.query)) {
            navigate(`/project/${results.singleProject}`, { replace: true });
        }
    }, [results.singleProject, query?.query, navigate]);

    const noResults =
        results.active.length === 0 &&
        results.selections.length === 0 &&
        results.organizations.length === 0 &&
        results.events.length === 0 &&
        results.content.length === 0 &&
        results.inactive.length === 0;

    return (
        <Main>
            <GenericHelmet
                title={'Søk'}
                description={'Spleis er en norsk folkefinanseringstjeneste. Søk på våre spleiser!'}
            >
                <div className={styles.container}>
                    <Search />
                    {hasSearchedForCurrentQuery && <Heading1 lookLike={3}>Søkeresultater</Heading1>}

                    {hasSearchedForCurrentQuery && noResults ? (
                        <div className={styles.feedbackContainer}>
                            <p>Fant dessverre ingenting på det du søkte etter.</p>
                            <p>
                                Sjekk at du ikke har noen skrivefeil, prøv mer generelle ord i søket eller andre ord som
                                kan bety det samme
                            </p>
                        </div>
                    ) : null}

                    {noResults && !query?.query && <SearchCampaignBanner />}

                    <div className={styles.searchResults}>
                        {results.content.length > 0 && (
                            <div className={styles.articleWrapper}>
                                <Heading2 lookLike={4}>Artikler</Heading2>
                                <ShowMore
                                    hideSteps={true}
                                    items={results.content}
                                    showCount={4}
                                    listCreator={(items) => (
                                        <div className={styles.articleResults}>
                                            {items.map((content) => (
                                                <div key={content.id} className={styles.articleContainer}>
                                                    <a href={content.id}>
                                                        <Heading3 lookLike={5}>{content.title}</Heading3>
                                                        <p>{content.introduction}</p>
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                />
                            </div>
                        )}

                        {(results.events.length > 0 || results.selections.length > 0) && (
                            <div className={styles.activeResults}>
                                <Heading2 lookLike={4}>Ting som skjer</Heading2>
                                <div className={styles.wrappingSearchResults}>
                                    {results.events.map((event) => (
                                        <div key={event.id}>
                                            <Link to={`/events/${event.id}`} className={styles.organizationContainer}>
                                                <ProfileInfo
                                                    title={event.name}
                                                    hideBadge
                                                    imageSize={40}
                                                    imageClassNameLeft={styles.organizationImage}
                                                    imageSrcLeft={event.imgUrl}
                                                    isEditable={false}
                                                    textContainerClassName={styles.profileTextContainer}
                                                />
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {results.active.length > 0 && (
                            <div className={styles.activeResults}>
                                <Heading2 lookLike={4}>Spleiser</Heading2>
                                <div className={styles.wrappingSearchResults}>
                                    <ProjectCardList
                                        projectIds={results.active}
                                        actionInformation="Search - active"
                                        cardTitleHeadingLevel={3}
                                    />
                                </div>
                            </div>
                        )}

                        {results.organizations.length > 0 && (
                            <div className={styles.activeResults}>
                                <Heading2 lookLike={4}>Organisasjoner</Heading2>
                                <div className={styles.wrappingSearchResults}>
                                    {results.organizations.map((o) => (
                                        <Link key={o.id} to={`/org/${o.id}`} className={styles.organizationContainer}>
                                            <ProfileInfo
                                                title={o.name}
                                                imageSize={40}
                                                hideBadge={true}
                                                imageClassNameLeft={styles.organizationImage}
                                                imageSrcLeft={o.imgUrl}
                                                hideLogo={!o.imgUrl}
                                                isEditable={false}
                                                textContainerClassName={styles.profileTextContainer}
                                            />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {results.inactive.length > 0 && (
                            <div className={styles.activeResults}>
                                <Heading2 lookLike={4}>Avsluttede spleiser</Heading2>
                                <div className={styles.wrappingSearchResults}>
                                    <ProjectCardList
                                        projectIds={results.inactive}
                                        actionInformation="Search - finished"
                                        cardTitleHeadingLevel={3}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </GenericHelmet>
        </Main>
    );
};

export const loader = async ({ dispatch }) => {
    await dispatch(fetchCMSAllEntriesByContentType({ contentType: CMS_CONTENT_TYPE_CAMPAIGN }));
    return null;
};

export default SearchWithResults;
